import React, { useState } from 'react';
import axios from 'axios';
import CustomSelect from '../widgets/CustomSelect';
import {
  CASH_REGISTER_EMPLOYEE_ENDPOINT,
  SECURITY_CODE_PATTERN,
} from './cashRegisterConfig';
import '../../styles/CashRegister.css';

const CloseRegisterModal = ({ session, onClose, onSuccess }) => {
  const [closingBalance, setClosingBalance] = useState('');
  const [employeeId, setEmployeeId] = useState(null);
  const [securityCode, setSecurityCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const balance = parseFloat(closingBalance);
    if (isNaN(balance) || balance < 0) {
      setError('Please enter a valid closing cash amount.');
      return;
    }
    if (!employeeId) {
      setError('Please select your name to sign.');
      return;
    }
    if (!SECURITY_CODE_PATTERN.test(securityCode)) {
      setError('Employee access code must be 4 to 8 letters or numbers.');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/cash-register/${session.id}/close`,
        {
          closing_balance: balance,
          closed_by_employee_id: employeeId,
          security_code: securityCode,
        }
      );
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to close register. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatAmount = (value) => {
    if (value === null || value === undefined) return '—';
    return Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="modal-overlay">
      <div className="cash-register-modal">
        <div className="modal-header">
          <h2>Close Register — {session.branch}</h2>
          <button className="modal-close" onClick={onClose} type="button">×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="cash-register-error">{error}</div>}
            <div className="close-summary">
              <p><strong>Opened by:</strong> {session.openedBy?.employee_name || '—'}</p>
              {session.openedByUser?.name && (
                <p><strong>Portal user:</strong> {session.openedByUser.name}</p>
              )}
              <p><strong>Opening balance:</strong> {formatAmount(session.opening_balance)}</p>
            </div>
            <p className="instruction-text">
              Count all cash in the register and enter the total below. Select your name and enter your personal access code to sign.
            </p>
            <div className="form-field">
              <label htmlFor="closing-balance">Closing cash amount *</label>
              <input
                id="closing-balance"
                type="number"
                min="0"
                step="0.01"
                value={closingBalance}
                onChange={(e) => setClosingBalance(e.target.value)}
                placeholder="0.00"
                required
              />
            </div>
            <div className="form-field">
              <label>Your name (signature) *</label>
              <CustomSelect
                name="closed_by_employee_id"
                value={employeeId}
                onChange={(name, value) => setEmployeeId(value)}
                endpoint={CASH_REGISTER_EMPLOYEE_ENDPOINT}
                valueField="employee_id"
                labelField="employee_name"
                placeholder="Search employee..."
                isSearchable={true}
              />
            </div>
            <div className="form-field">
              <label htmlFor="close-security-code">Employee access code *</label>
              <input
                id="close-security-code"
                type="password"
                className="access-code-input"
                value={securityCode}
                onChange={(e) => setSecurityCode(e.target.value)}
                maxLength={8}
                autoComplete="off"
                placeholder="Enter your access code"
                required
              />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" type="button" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              className="btn-primary"
              type="submit"
              disabled={submitting || !employeeId || !SECURITY_CODE_PATTERN.test(securityCode)}
            >
              {submitting ? 'Closing...' : 'Close Register'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CloseRegisterModal;
